/** @jsxImportSource @emotion/react */

import { jsonToCsv } from '@quickbit/backoffice-core-frontend';
import { UtilityButton } from '@quickbit/qb-design-system';
import { useAsyncFn } from 'react-use';
import { MerchantOrderFilters, SortOptions } from 'tabs/MerchantOrders/types';
import { getOrder, searchOrders } from '../api';

const PAGE_SIZE = 20;

interface Props {
  filters: MerchantOrderFilters;
  sortOptions?: SortOptions;
}

type Columns =
  | 'Order ID'
  | 'Reference ID'
  | 'Blockchain transaction ID'
  | 'Amount'
  | 'Currency'
  | 'Created At'
  | 'Completed At'
  | 'Status'
  | 'Merchant ID'
  | 'Merchant Name'
  | 'Order Type'
  | 'Active'
  | 'Errors';

type Row = Record<Columns, string | number | null>;

const orderToRow = async (orderId: string): Promise<Row> => {
  const order = await getOrder(orderId);
  return {
    'Order ID': order.uuid,
    'Reference ID': order.referenceId,
    'Blockchain transaction ID':
      (order.extraData?.blockchainTransactionId as string | undefined) ?? '',
    Amount: order.amount,
    Currency: order.currency,
    'Created At': order.createdAt.toLocaleString(),
    'Completed At': order.completedAt?.toLocaleString() ?? '',
    Status: order.status,
    'Merchant ID': order.merchant.uuid,
    'Merchant Name': order.merchant.name,
    'Order Type': order.useProcessingFlow
      ? 'Processing'
      : order.useOnrampFlow
      ? 'Onramp'
      : 'Pay',
    Active: order.active ? 'true' : 'false',
    Errors: `"${order.errors
      .map((error) => {
        // Stringify the details object and escape double quotes by replacing them with two double quotes
        const detailsString = JSON.stringify(error.details).replace(/"/g, '""');
        // Escape double quotes in errorCode and concatenate errorCode with details
        const errorString = `${error.errorCode.replace(
          /"/g,
          '""'
        )}: ${detailsString}`;
        return errorString;
      })
      .join('| ')}"`, // Join all errors with a pipe and a space for readability
  };
};

export const ExportCsv = ({ filters, sortOptions }: Props) => {
  const [{ loading: downloadCsvLoading }, downloadCsv] =
    useAsyncFn(async () => {
      const page = 1;
      const firstOrders = await searchOrders({
        pagination: { size: PAGE_SIZE, page },
        filters,
        sortOptions,
      });
      const lastPage = Math.ceil(
        firstOrders.aggregatedData.orderCount / PAGE_SIZE
      );
      let data: Row[] = await Promise.all(
        firstOrders.orders.map((order) => orderToRow(order.uuid))
      );

      let i = page + 1;
      let errorCount = 0;
      while (i <= lastPage) {
        try {
          const orders = await searchOrders({
            pagination: { size: PAGE_SIZE, page: i },
            filters,
            sortOptions,
          });
          const ordersDetails = await Promise.all(
            orders.orders.map((order) => orderToRow(order.uuid))
          );
          data = data.concat(ordersDetails);
          i++;
          errorCount = 0;
        } catch (e) {
          errorCount++;
          if (errorCount >= 5) {
            throw new Error('Failed to fetch order data');
          }
          console.error('Failed to fetch order data, trying again', e);
        }
      }

      jsonToCsv({
        data: data,
        filename: 'orders',
        delimiter: ';',
        columns: [
          'Order ID',
          'Reference ID',
          'Blockchain transaction ID',
          'Amount',
          'Currency',
          'Created At',
          'Completed At',
          'Status',
          'Merchant ID',
          'Merchant Name',
          'Order Type',
          'Active',
          'Errors',
        ] as Columns[],
      });
      return data;
    }, [filters, sortOptions]);

  return (
    <UtilityButton
      text="Extract as csv"
      onClick={downloadCsv}
      loading={downloadCsvLoading}
      disabled={downloadCsvLoading}
    />
  );
};
